import React, {useContext, useState, useEffect} from "react";
import {QuotationDraftWithProductsSchema, QuotationReadonlyProductSchema} from "../types";
import QuotationDraft from "./quotation-draft";
import {SurveyProductItemSchema, SurveyProductsSchema} from "../../commons";
import {ProductsSchema, UserReducedSchema} from "../../types";
import CountryContext from "../../../CountryContext";
import useCountryAxiosInstance from "../../../hooks/useCountryAxiosInstance";
import {Redirect, useHistory, useParams} from "react-router-dom";
import Spinner from "ui-components/Spinner";
import AuthenticationService from "../../../services/AuthenticationService";
import NotFound from "../../../ui-components/pages/NotFound";
import {useQuotations} from "../hooks/useQuotations";
import {getProductCode} from "./lib/utilities";
import getKColorResultContextKey from "../../../utils/getKColorResultContextKey";

export const ViewEditQuotationDraft = () => {
  // params
  const {
    quotation_id,
    draft_id,
    duplicate,
  } = useParams<Record<string, string | undefined>>();

  // context
  const {country: {code}} = useContext(CountryContext);

  // hooks
  const countryApiRequest = useCountryAxiosInstance();
  const history = useHistory();
  const {
    fetchBaseDraftInfo,
  } = useQuotations();

  // state
  const [data, setData]
    = useState<QuotationDraftWithProductsSchema | null>(null);
  const [showNotFound, setShowNotFound] = useState<boolean>(false);
  const [artificialProducts, setArtificialProducts] = useState<SurveyProductsSchema | undefined>(undefined);
  const [baseDraftInfo, setBaseDraftInfo]
    = useState<{id: number | null, message: string}>();

  // utilities
  const userData = AuthenticationService.getUserData();
  const userScopes = AuthenticationService.getUserScopes();
  const copyQuotation = duplicate === 'duplicate';
  const isNewDraft = draft_id === "new";
  const getUser = (key: 'des_agent' | 'des_resale' | 'des_enterprise') => {
    const field = key === 'des_agent' ? 'name' : 'company';
    return copyQuotation
      ? undefined :
      !!data?.quotation[key]
        // @ts-ignore
        ? {[field]: data.quotation[key]} as UserReducedSchema
        : undefined
  }

  useEffect(() => {
    // On page load, fetch the base draft info
    fetchBaseDraftInfo(quotation_id).then(setBaseDraftInfo);
  }, [quotation_id]);


  useEffect(() => {
    // On page load, fetch the target quotation draft
    (async () => {
      try {
        const url = !isNewDraft
          ? `/quotations/draft/${draft_id}`
          : `/quotations/${quotation_id}/draft/base`;
        const quotationDraft: QuotationDraftWithProductsSchema = await countryApiRequest!.get(url);
        setData(quotationDraft);
      } catch (error: any) {
        if (error.response.status) {
          setShowNotFound(true);
          return;
        }
        console.error(error);
      }
    })();
  }, [draft_id]);

  useEffect(() => {
    if (data?.products)
      setArtificialProducts(generateArtificialProducts());
  }, [data]);

  if (!userScopes.includes('quotation:create_draft') && isNewDraft)
    return <Redirect to={`/${code}/quotations`} />


  const generateQuotationProductOverrides = (qp: QuotationReadonlyProductSchema) => {
    const mapper = {
      product_des_it: 'art_des_it',
      val_product_unitprice: 'prezzo',
      product_min_qty: 'min_qty',
      product_resa: 'resa',
    } as {[key: string]: string};
    return Object.entries(mapper).reduce(
      (acc, [qpk, pk]) => {
        const value = qp[qpk as keyof QuotationReadonlyProductSchema];
        return {
          ...acc,
          ...(!!value || value === 0 ? {[pk]: value} : {})
        };
      },
      {} as {[key: string]: any},
    );
  };
  const generateArtificialProducts = () => {
    if (!data?.products)
      return undefined;

    return data.products.reduce(
      (acc, curr) => {
        let code:string | undefined;
        // determine key with which to store the product @todo make it a utility function
        if (curr.is_kcolor) {
          if (curr.products) {
            code = getKColorResultContextKey(
              curr.products.code,
              curr.kcolor_color_code!,
              curr.products.min_qty!,
            );
            if (!code) {
              return acc;
            }
          } else {
            return acc;
          }
        } else {
          code = curr.cod_product
        }

        return {
          ...acc,
          [code]: {
            product: {
              ...curr.products,
              ...generateQuotationProductOverrides(curr),
            } as ProductsSchema,
            quantity: curr.val_product_qt,
            isKcc: curr.is_kcc,
            isKcolor: curr.is_kcolor,
            kcolor_color_code: curr.kcolor_color_code,
            kcolor_color_rgb: curr.kcolor_color_rgb,
            kerakoll_price: curr.kerakoll_price,
            tintometer_price: curr.tintometer_price,
            surveys: curr.surveys,
          } as SurveyProductItemSchema,
        };
      },
      {} as SurveyProductsSchema,
    );
  }

  if (!data)
    return <Spinner classes={undefined} styleType={undefined} />;

  if (showNotFound)
    return <NotFound />;

  return (
    <QuotationDraft
      editable={copyQuotation || isNewDraft}
      data={
        copyQuotation
          ? undefined
          : data.quotation
      }
      agent={getUser('des_agent')!}
      resale={getUser('des_resale')}
      enterprise={getUser('des_enterprise')}
      worksite={data.quotation.worksites}
      surveys={data.quotation.surveys}
      products={artificialProducts}
      setProducts={setArtificialProducts}
      loading={false}
      onHistoryBackClick={() => {
        history.push(`/${code}/quotations/${data.quotation.id}`);
      }}
      fullScreen
      onSave={() => {
        history.push(`/${code}/quotations/${data.quotation.id}`)
      }}
      copyQuotation={copyQuotation}
      agentId={data.quotation.id_agent}
      quotationProducts={data.products}
      showVisibilityDisclaimer={
        userData.id === data.id_creator
        && !data.flg_public_changes
      }
      notes={!isNewDraft ? data?.notes : undefined}
      draftId={!!draft_id && draft_id !== 'new' ? parseInt(draft_id) : undefined}
      baseDraftInfo={baseDraftInfo}
      kColorPriceChoose={data.quotation.kcolor_chosen_price}
    />
  )
}